import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@/utils/icons';
import { cn } from '@/lib/utils';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const SheetClose = DialogPrimitive.Close;
const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'dls-fixed dls-inset-0 dls-z-50 dls-bg-white/30 dls-backdrop-blur-sm data-[state=open]:dls-animate-in data-[state=closed]:dls-animate-out data-[state=closed]:dls-fade-out-0 data-[state=open]:dls-fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = DialogPrimitive.Overlay.displayName;

const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'dls-fixed dls-z-50 dls-gap-4 dls-bg-background dls-shadow-40 dls-transition dls-ease-in-out data-[state=open]:dls-animate-in data-[state=closed]:dls-animate-out data-[state=closed]:dls-duration-300 data-[state=open]:dls-duration-500',
        'dls-inset-y-0 dls-right-0 dls-h-full dls-w-full dls-max-w-lg dls-border-l data-[state=closed]:dls-slide-out-to-right data-[state=open]:dls-slide-in-from-right',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="dls-absolute dls-right-4 dls-top-4 dls-rounded-sm dls-opacity-70 dls-ring-offset-background dls-transition-opacity hover:dls-opacity-100 focus:dls-outline-none focus:dls-ring-2 focus:dls-ring-ring focus:dls-ring-offset-2">
        <X className="dls-h-4 dls-w-4" />
        <span className="dls-sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = DialogPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('dls-flex dls-flex-col dls-space-y-2 dls-text-center sm:dls-text-left', className)} {...props} />
);

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('dls-text-lg dls-font-semibold dls-text-foreground', className)}
    {...props}
  />
));
SheetTitle.displayName = DialogPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('dls-text-sm dls-text-muted-foreground', className)}
    {...props}
  />
));
SheetDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
};
